import type { SourceTurnV1 } from './contracts';
import { CAPTION_HYDRATION_LIMITS } from './captionHydration';
import type { EngineCoordinateV1 } from './engineCoordinate';
import { isLaunchLanguageCode, type LaunchLanguageCode } from './launchLanguages';
import type { AttachedTranslationRevisionV1, RevisionUnavailableReasonCode } from './revisionEnvelope';
import {
  parseCaptionHydrationV3Cursor,
  validateCaptionHydrationV3Page,
  type CaptionHydrationV3Selection,
  type ParsedCursor,
} from './captionHydrationPageV3';

export interface CaptionHydrationCollectorScope {
  generation: number;
  connectionGeneration: number;
  callUuid: string;
  credentialLineage: string | null;
  targetLanguage: LaunchLanguageCode;
  captionFactOrderAtStart: number;
}

export interface CaptionHydrationSourceItem {
  source_turn: SourceTurnV1;
  transcript_sync_id: string;
}
export interface CaptionHydrationRevisionItem {
  revision: AttachedTranslationRevisionV1;
  coordinate: EngineCoordinateV1 | null;
}
export interface CaptionHydrationSelectedItem extends CaptionHydrationSourceItem,
  CaptionHydrationRevisionItem {
  selection: CaptionHydrationV3Selection;
}
export interface CaptionHydrationUnavailableItem {
  source_turn_id: string;
  target_language: string;
  reason_code: RevisionUnavailableReasonCode;
}
/** Exact terminal server fields plus the source identities traversed under its
 * visibility snapshot. This proves a frozen server prefix, never later live
 * delivery completeness or current sender authority. No credential is retained. */
export interface CaptionSourceHistoryEvidence {
  contract_version: 'live-translation.caption-hydration-page.v3';
  call_id: string;
  target_language: LaunchLanguageCode;
  visibility_snapshot: string;
  complete: boolean;
  outcome: 'page' | 'snapshot_changed';
  source_turn_ids: readonly string[];
}

export function validateCaptionSourceHistoryEvidence(value: unknown, callId: string,
  sources: readonly CaptionHydrationSourceItem[]): value is CaptionSourceHistoryEvidence {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  const keys = ['contract_version', 'call_id', 'target_language', 'visibility_snapshot',
    'complete', 'outcome', 'source_turn_ids'];
  if (Object.keys(v).length !== keys.length || Object.keys(v).some(key => !keys.includes(key))
      || v.contract_version !== 'live-translation.caption-hydration-page.v3' || v.call_id !== callId
      || !isLaunchLanguageCode(v.target_language)
      || !((v.complete === true && v.outcome === 'page')
        || (v.complete === false && v.outcome === 'snapshot_changed'))
      || !Array.isArray(v.source_turn_ids) || v.source_turn_ids.length !== sources.length
      || !sources.every((item, index) => item?.source_turn?.call_id === callId
        && item.source_turn.source_turn_id === (v.source_turn_ids as unknown[])[index])) return false;
  // Reuse the real server-cursor syntax, not a fabricated timestamp/revision.
  return parseCaptionHydrationV3Cursor({ contract_version: 'live-translation.caption-hydration-cursor.v3',
    call_id: callId, target_language: v.target_language, visibility_snapshot: v.visibility_snapshot,
    phase: 'source_turns' }, { callUuid: callId, targetLanguage: v.target_language }) !== null;
}

export interface CaptionHydrationFrozenPrefix extends Readonly<CaptionHydrationCollectorScope> {
  visibilitySnapshot: string;
  sourceHistory: Readonly<CaptionSourceHistoryEvidence>;
  sourceTurns: readonly CaptionHydrationSourceItem[];
  revisions: readonly CaptionHydrationRevisionItem[];
  selections: readonly CaptionHydrationSelectedItem[];
  unavailable: readonly CaptionHydrationUnavailableItem[];
}
export type CaptionHydrationCollectorOutcome =
  | { outcome: 'continue' | 'retry' | 'stale' }
  | { outcome: 'frozen-prefix' | 'frozen-prefix-needs-catchup'; prefix: CaptionHydrationFrozenPrefix };
export interface CaptionHydrationCollectorPorts {
  isCurrent: (scope: Readonly<CaptionHydrationCollectorScope>) => boolean;
}

// Database UUID format; do not invent version/variant restrictions for call IDs.
const DATABASE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const nonnegativeCount = (value: unknown) => Number.isSafeInteger(value) && (value as number) >= 0;

/** Own JSON structure without serializing or copying immutable caption strings.
 * Materialize values before validation, so later caller mutations cannot change
 * accepted facts. Sparse arrays become dense and fail the actual DTO validator.
 * Cycles are outside the JSON transport contract, not an invented depth limit. */
function ownJson(value: unknown, ancestors = new WeakSet<object>()): unknown {
  if (value === null || typeof value !== 'object') return value;
  if (ancestors.has(value)) throw new Error('caption_hydration_non_json_body');
  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      const owned: unknown[] = [];
      for (const item of value) owned.push(ownJson(item, ancestors));
      return owned;
    }
    // Spread defines own data properties, including an untrusted __proto__ key;
    // later assignments below cannot invoke Object.prototype's setter.
    const owned = { ...(value as Record<string, unknown>) };
    for (const key of Object.keys(owned)) owned[key] = ownJson(owned[key], ancestors);
    return owned;
  } finally {
    ancestors.delete(value);
  }
}
function freezeOwned(value: unknown): void {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) return;
  for (const child of Object.values(value)) freezeOwned(child);
  Object.freeze(value);
}
/** Compare validated JSON values exactly, independent of object key order.
 * Annotation arrays retain element order and every nested note. Caption strings
 * are immutable scalars: do not normalize or serialize their complete contents. */
function equalJsonValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && left.length === right.length &&
      left.every((item, index) => equalJsonValue(item, right[index]));
  }
  const a = left as Record<string, unknown>, b = right as Record<string, unknown>;
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) =>
    Object.prototype.hasOwnProperty.call(b, key) && equalJsonValue(a[key], b[key]));
}
function equalRecord(left: object, right: object): boolean {
  return equalJsonValue(left, right);
}
function equalCoordinate(a: EngineCoordinateV1 | null, b: EngineCoordinateV1 | null): boolean {
  return a === null || b === null ? a === b : equalRecord(a, b);
}
function coordinateMatchesSource(coordinate: EngineCoordinateV1 | null, source: SourceTurnV1): boolean {
  return coordinate === null || (coordinate.call_id === source.call_id &&
    coordinate.source_turn_id === source.source_turn_id &&
    coordinate.source_stable_participant_id === source.stable_participant_id &&
    coordinate.source_epoch === source.source_epoch &&
    coordinate.source_track_sid === source.source_track_sid);
}
function reserve(keys: string[], existing: ReadonlySet<string>, staged: Set<string>): boolean {
  for (const key of keys) {
    if (existing.has(key) || staged.has(key)) return false;
    staged.add(key);
  }
  return true;
}

/** Exhaustive historical state, separate from renewable page-request identity.
 * The caller must check its page ownership immediately before acceptPage. Scope
 * remains private lifecycle data: do not dispatch its credential lineage into
 * Redux, DOM or telemetry. This class installs no facts or live sender authority. */
export class CaptionHydrationFrozenPrefixCollector {
  private readonly scope: Readonly<CaptionHydrationCollectorScope>;
  private readonly scopeValid: boolean;
  private readonly isCurrentScope: CaptionHydrationCollectorPorts['isCurrent'];
  private cursor: ParsedCursor | null = null;
  private readonly cursors = new Set<string>();
  private finished = false;
  private readonly sources = new Map<string, CaptionHydrationSourceItem>();
  private readonly sourceKeys = new Set<string>();
  private readonly participantRoles = new Map<string, SourceTurnV1['speaker_role']>();
  private readonly epochTracks = new Map<string, string>();
  private readonly revisionById = new Map<string, CaptionHydrationRevisionItem>();
  private readonly revisionKeys = new Set<string>();
  private readonly selectionKeys = new Set<string>();
  private readonly unavailableKeys = new Set<string>();
  private readonly sourceTurns: CaptionHydrationSourceItem[] = [];
  private readonly revisions: CaptionHydrationRevisionItem[] = [];
  private readonly selections: CaptionHydrationSelectedItem[] = [];
  private readonly unavailable: CaptionHydrationUnavailableItem[] = [];

  constructor(scope: CaptionHydrationCollectorScope, ports: CaptionHydrationCollectorPorts) {
    this.scope = Object.freeze({
      generation: scope.generation, connectionGeneration: scope.connectionGeneration,
      callUuid: scope.callUuid, credentialLineage: scope.credentialLineage,
      targetLanguage: scope.targetLanguage, captionFactOrderAtStart: scope.captionFactOrderAtStart,
    });
    const captured = this.scope;
    this.scopeValid = typeof captured.callUuid === 'string' && DATABASE_UUID.test(captured.callUuid) &&
      isLaunchLanguageCode(captured.targetLanguage) && nonnegativeCount(captured.generation) &&
      nonnegativeCount(captured.connectionGeneration) && nonnegativeCount(captured.captionFactOrderAtStart) &&
      typeof captured.credentialLineage === 'string' && captured.credentialLineage.length > 0;
    this.isCurrentScope = ports.isCurrent;
  }

  getCursor(): Record<string, unknown> | null {
    return this.cursor === null ? null : { ...this.cursor.raw };
  }

  acceptPage(raw: unknown): CaptionHydrationCollectorOutcome {
    if (this.finished || !this.isCurrentScope(this.scope)) return { outcome: 'stale' };
    if (!this.scopeValid) return { outcome: 'retry' };
    // Refuse the actual 100-row page contract before materializing its JSON
    // structure. This is not a whole-prefix row, page, byte or text limit.
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return { outcome: 'retry' };
    const incomingItems = (raw as Record<string, unknown>).items;
    if (!Array.isArray(incomingItems) || incomingItems.length > CAPTION_HYDRATION_LIMITS.rpcPageSize) {
      return { outcome: 'retry' };
    }
    let owned: unknown;
    try { owned = ownJson(raw); } catch { return { outcome: 'retry' }; }
    const checked = validateCaptionHydrationV3Page(owned, {
      callUuid: this.scope.callUuid, targetLanguage: this.scope.targetLanguage, cursor: this.cursor,
    });
    if (!checked.valid) return { outcome: 'retry' };
    const page = checked.page;
    const next = page.next_cursor === null ? null : parseCaptionHydrationV3Cursor(page.next_cursor, {
      callUuid: this.scope.callUuid, targetLanguage: this.scope.targetLanguage,
    });
    if (page.next_cursor !== null && (next === null || this.cursors.has(next.canonical))) {
      return { outcome: 'retry' };
    }

    const stagedSources = new Map<string, CaptionHydrationSourceItem>();
    const stagedSourceKeys = new Set<string>();
    const stagedRoles = new Map<string, SourceTurnV1['speaker_role']>();
    const stagedTracks = new Map<string, string>();
    const stagedRevisions = new Map<string, CaptionHydrationRevisionItem>();
    const stagedRevisionKeys = new Set<string>();
    const stagedSelections = new Set<string>();
    const stagedSelectedItems: CaptionHydrationSelectedItem[] = [];
    const stagedUnavailable = new Set<string>();
    if (page.phase === 'source_turns') {
      for (const item of page.items as CaptionHydrationSourceItem[]) {
        const source = item.source_turn, id = source.source_turn_id;
        // Current participant and epoch UNIQUE/composite-FK targets in
        // 20261090/92 and the 20261149 writer bind these exact facts. Distinct
        // participants/epochs may legitimately have different roles/tracks.
        const participantKey = JSON.stringify([source.call_id, source.stable_participant_id]);
        const epochKey = JSON.stringify([source.call_id, source.stable_participant_id, source.source_epoch]);
        const role = this.participantRoles.get(participantKey) ?? stagedRoles.get(participantKey);
        const track = this.epochTracks.get(epochKey) ?? stagedTracks.get(epochKey);
        if ((role !== undefined && role !== source.speaker_role) ||
            (track !== undefined && track !== source.source_track_sid)) return { outcome: 'retry' };
        if (this.sources.has(id) || stagedSources.has(id) || !reserve([
          JSON.stringify(['ordinal', source.call_id, source.stable_participant_id, source.source_epoch, source.ordinal]),
          JSON.stringify(['segment', source.call_id, source.stable_participant_id, source.source_epoch, source.deepgram_segment_id]),
          JSON.stringify(['sync', source.call_id, item.transcript_sync_id]),
        ], this.sourceKeys, stagedSourceKeys)) return { outcome: 'retry' };
        stagedSources.set(id, item);
        stagedRoles.set(participantKey, source.speaker_role);
        stagedTracks.set(epochKey, source.source_track_sid);
      }
    } else if (page.phase === 'revisions') {
      for (const item of page.items as CaptionHydrationRevisionItem[]) {
        const revision = item.revision, id = revision.translation_revision_id;
        const source = this.sources.get(revision.source_turn_id);
        if (!source || !coordinateMatchesSource(item.coordinate, source.source_turn) ||
            this.revisionById.has(id) || stagedRevisions.has(id) || !reserve([
              JSON.stringify([revision.source_turn_id, revision.target_language, revision.revision_index]),
            ], this.revisionKeys, stagedRevisionKeys)) return { outcome: 'retry' };
        stagedRevisions.set(id, item);
      }
    } else if (page.phase === 'selections') {
      for (const item of page.items as CaptionHydrationSelectedItem[]) {
        const source = this.sources.get(item.source_turn.source_turn_id);
        const revision = this.revisionById.get(item.revision.translation_revision_id);
        if (!source || !revision || source.transcript_sync_id !== item.transcript_sync_id ||
            !equalRecord(source.source_turn, item.source_turn) ||
            !equalRecord(revision.revision, item.revision) ||
            !equalCoordinate(revision.coordinate, item.coordinate) || !reserve([
              JSON.stringify([item.selection.source_turn_id, item.selection.target_language]),
            ], this.selectionKeys, stagedSelections)) return { outcome: 'retry' };
        // Full equality is verified FIRST. Reuse only our already owned,
        // immutable records so selected bundles do not retain duplicate copies
        // of the complete source/revision words from a later response body.
        stagedSelectedItems.push(Object.freeze({
          source_turn: source.source_turn, transcript_sync_id: source.transcript_sync_id,
          revision: revision.revision, coordinate: revision.coordinate, selection: item.selection,
        }));
      }
    } else {
      for (const item of page.items as CaptionHydrationUnavailableItem[]) {
        // The persisted marker relation intentionally permits orphan source IDs.
        if (!reserve([JSON.stringify([item.source_turn_id, item.target_language])],
            this.unavailableKeys, stagedUnavailable)) return { outcome: 'retry' };
      }
    }
    if (!this.isCurrentScope(this.scope)) return { outcome: 'stale' };
    freezeOwned(page);
    // Commit only after the WHOLE page passes. No aggregate maps/arrays are
    // copied per page: work and retained ownership scale with incoming records.
    for (const [id, item] of stagedSources) this.sources.set(id, item);
    for (const key of stagedSourceKeys) this.sourceKeys.add(key);
    for (const [key, role] of stagedRoles) this.participantRoles.set(key, role);
    for (const [key, track] of stagedTracks) this.epochTracks.set(key, track);
    for (const [id, item] of stagedRevisions) this.revisionById.set(id, item);
    for (const key of stagedRevisionKeys) this.revisionKeys.add(key);
    for (const key of stagedSelections) this.selectionKeys.add(key);
    for (const key of stagedUnavailable) this.unavailableKeys.add(key);
    if (page.phase === 'source_turns') this.sourceTurns.push(...page.items as CaptionHydrationSourceItem[]);
    else if (page.phase === 'revisions') this.revisions.push(...page.items as CaptionHydrationRevisionItem[]);
    else if (page.phase === 'selections') this.selections.push(...stagedSelectedItems);
    else this.unavailable.push(...page.items as CaptionHydrationUnavailableItem[]);
    if (next !== null) {
      this.cursor = next;
      this.cursors.add(next.canonical);
      return { outcome: 'continue' };
    }
    this.cursor = null;
    this.finished = true;
    const prefix: CaptionHydrationFrozenPrefix = Object.freeze({
      ...this.scope, visibilitySnapshot: page.visibility_snapshot,
      sourceHistory: Object.freeze({ contract_version: page.contract_version,
        call_id: page.call_id, target_language: this.scope.targetLanguage,
        visibility_snapshot: page.visibility_snapshot, complete: page.complete, outcome: page.outcome,
        source_turn_ids: Object.freeze(this.sourceTurns.map(item => item.source_turn.source_turn_id)) }),
      sourceTurns: Object.freeze(this.sourceTurns.slice()),
      revisions: Object.freeze(this.revisions.slice()),
      selections: Object.freeze(this.selections.slice()),
      unavailable: Object.freeze(this.unavailable.slice()),
    });
    return { outcome: page.outcome === 'snapshot_changed'
      ? 'frozen-prefix-needs-catchup' : 'frozen-prefix', prefix };
  }
}
